import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { withLocalStorageAuth } from '../utils/localStorageAuth';

const PropertyContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'yet2book_selected_property';

export const PropertyProvider = ({ children }) => {
  const { user, token, isAuthenticated, isSessionRestored } = useSelector((state) => state.auth);
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${BACKEND_URL}/api/v2/properties`,
        withLocalStorageAuth({}, token),
      );

      setProperties(response.data || []);
    } catch (err) {
      console.error('Failed to load properties');
      setError(err.response?.data?.detail || 'Failed to load properties');
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch only after the local-storage-backed Partner identity is available.
  useEffect(() => {
    if (isSessionRestored && isAuthenticated && user?.role === 'PARTNER' && token) {
      fetchProperties();
    } else if (isSessionRestored) {
      setProperties([]);
      setLoading(false);
    }
  }, [fetchProperties, isAuthenticated, isSessionRestored, token, user?.role]);

  // Auto-select property based on business logic
  useEffect(() => {
    if (properties.length > 0 && !selectedPropertyId) {
      // Try to restore from localStorage
      const savedPropertyId = localStorage.getItem(STORAGE_KEY);
      
      if (savedPropertyId && properties.find(p => p.id === savedPropertyId)) {
        // Restore saved selection if property still exists
        setSelectedPropertyId(savedPropertyId);
      } else if (properties.length === 1) {
        // Auto-select if only one property (no selector shown)
        setSelectedPropertyId(properties[0].id);
      }
      // If multiple properties and no saved selection, leave null (show "All Properties")
    }
  }, [properties]);

  // Persist selection to localStorage
  useEffect(() => {
    if (selectedPropertyId) {
      localStorage.setItem(STORAGE_KEY, selectedPropertyId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedPropertyId]);

  const selectProperty = (propertyId) => {
    setSelectedPropertyId(propertyId);
  };

  const clearSelection = () => {
    setSelectedPropertyId(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const getSelectedProperty = () => {
    return properties.find(p => p.id === selectedPropertyId) || null;
  };

  const hasMultipleProperties = () => {
    return properties.length > 1;
  };

  const value = {
    properties,
    selectedPropertyId,
    selectedProperty: getSelectedProperty(),
    loading,
    error,
    selectProperty,
    clearSelection,
    hasMultipleProperties: hasMultipleProperties(),
    refreshProperties: fetchProperties
  };

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
};

export const usePropertyContext = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('usePropertyContext must be used within PropertyProvider');
  }
  return context;
};

export default PropertyContext;

